import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import { computed, PropType } from 'vue'
import { ElementTypes, PPTElement } from '@/types/slides'
import { ContextmenuItem } from '@/components/Contextmenu/types'

import useLockElement from '@/hooks/useLockElement'
import useDeleteElement from '@/hooks/useDeleteElement'
import useCombineElement from '@/hooks/useCombineElement'
import useOrderElement from '@/hooks/useOrderElement'
import useAlignElementToCanvas from '@/hooks/useAlignElementToCanvas'
import useCopyAndPasteElement from '@/hooks/useCopyAndPasteElement'
import useSelectAllElement from '@/hooks/useSelectAllElement'

import { ElementOrderCommands, ElementAlignCommands } from '@/types/edit'

import ImageElement from '@/views/components/element/ImageElement/index.vue'
import TextElement from '@/views/components/element/TextElement/index.vue'
import ShapeElement from '@/views/components/element/ShapeElement/index.vue'
import LineElement from '@/views/components/element/LineElement/index.vue'
import ChartElement from '@/views/components/element/ChartElement/index.vue'
import TableElement from '@/views/components/element/TableElement/index.vue'
import LatexElement from '@/views/components/element/LatexElement/index.vue'
import VideoElement from '@/views/components/element/VideoElement/index.vue'
import AudioElement from '@/views/components/element/AudioElement/index.vue'
import { useI18n } from 'vue-i18n'


export default /*#__PURE__*/_defineComponent({
  __name: 'EditableElement',
  props: {
  elementInfo: {
    type: Object as PropType<PPTElement>,
    required: true,
  },
  elementIndex: {
    type: Number,
    required: true,
  },
  isMultiSelect: {
    type: Boolean,
    required: true,
  },
  selectElement: {
    type: Function as PropType<(e: MouseEvent | TouchEvent, element: PPTElement, canMove?: boolean) => void>,
    required: true,
  },
  openLinkDialog: {
    type: Function as PropType<() => void>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const { t, locale } = useI18n()


const currentElementComponent = computed(() => {
  const elementTypeMap = {
    [ElementTypes.IMAGE]: ImageElement,
    [ElementTypes.TEXT]: TextElement,
    [ElementTypes.SHAPE]: ShapeElement,
    [ElementTypes.LINE]: LineElement,
    [ElementTypes.CHART]: ChartElement,
    [ElementTypes.TABLE]: TableElement,
    [ElementTypes.LATEX]: LatexElement,
    [ElementTypes.VIDEO]: VideoElement,
    [ElementTypes.AUDIO]: AudioElement,
  }
  return elementTypeMap[props.elementInfo.type] || null
})

const { orderElement } = useOrderElement()
const { alignElementToCanvas } = useAlignElementToCanvas()
const { combineElements, uncombineElements } = useCombineElement()
const { deleteElement } = useDeleteElement()
const { lockElement, unlockElement } = useLockElement()
const { copyElement, pasteElement, cutElement } = useCopyAndPasteElement()
const { selectAllElement } = useSelectAllElement()

const contextmenus = (): ContextmenuItem[] => {
  if (props.elementInfo.lock) {
    return [{
      text: t(`editor.positionPanel.unlock`), 
      handler: () => unlockElement(props.elementInfo),
    }]
  }

  return [
    {
      text: t(`editor.ctrlX`),
      subText: 'Ctrl + X',
      handler: cutElement,
    },
    {
      text: t(`editor.ctrlC`),
      subText: 'Ctrl + C',
      handler: copyElement,
    },
    {
      text: t(`editor.ctrlV`),
      subText: 'Ctrl + V',
      handler: pasteElement,
    },
    { divider: true },
    {
      text: t(`editor.positionPanel.horizontalCenter`),//水平居中
      handler: () => alignElementToCanvas(ElementAlignCommands.HORIZONTAL),
      children: [
        { text: t(`editor.positionPanel.horizontalVerticalCenter`), handler: () => alignElementToCanvas(ElementAlignCommands.CENTER), },//水平垂直居中
        { text: t(`editor.positionPanel.horizontalCenter`), handler: () => alignElementToCanvas(ElementAlignCommands.HORIZONTAL) },//水平居中
        { text: t(`editor.positionPanel.left`), handler: () => alignElementToCanvas(ElementAlignCommands.LEFT) },//左对齐
        { text: t(`editor.positionPanel.right`), handler: () => alignElementToCanvas(ElementAlignCommands.RIGHT) },//右对齐
      ],
    },
    {
      text: t(`editor.positionPanel.verticalCenter`),//垂直居中
      handler: () => alignElementToCanvas(ElementAlignCommands.VERTICAL),
      children: [
        { text: t(`editor.positionPanel.horizontalVerticalCenter`), handler: () => alignElementToCanvas(ElementAlignCommands.CENTER) },//水平垂直居中
        { text: t(`editor.positionPanel.verticalCenter`), handler: () => alignElementToCanvas(ElementAlignCommands.VERTICAL) },//垂直居中
        { text: t(`editor.positionPanel.top`), handler: () => alignElementToCanvas(ElementAlignCommands.TOP) },//上对齐
        { text: t(`editor.positionPanel.bottom`), handler: () => alignElementToCanvas(ElementAlignCommands.BOTTOM) },//下对齐
      ],
    },
    { divider: true },
    {
      text: t(`editor.positionPanel.onTop`),//置于顶层
      disable: props.isMultiSelect && !props.elementInfo.groupId,
      handler: () => orderElement(props.elementInfo, ElementOrderCommands.TOP),
      children: [
        { text: t(`editor.positionPanel.onTop`), handler: () => orderElement(props.elementInfo, ElementOrderCommands.TOP) },//置于顶层
        { text: t(`editor.positionPanel.levelUp`), handler: () => orderElement(props.elementInfo, ElementOrderCommands.UP) },//上移一层
      ],
    },
    {
      text: t(`editor.positionPanel.onBottom`),//置于底层
      disable: props.isMultiSelect && !props.elementInfo.groupId,
      handler: () => orderElement(props.elementInfo, ElementOrderCommands.BOTTOM),
      children: [
        { text: t(`editor.positionPanel.onBottom`), handler: () => orderElement(props.elementInfo, ElementOrderCommands.BOTTOM) },//置于底层
        { text: t(`editor.positionPanel.levelDown`), handler: () => orderElement(props.elementInfo, ElementOrderCommands.DOWN) },//下移一层
      ],
    },
    { divider: true },
    {
      text: t(`editor.positionPanel.setLink`),//设置链接
      handler: props.openLinkDialog,
    },
    {
      text: props.elementInfo.groupId ? t(`editor.positionPanel.ungroup`) : t(`editor.positionPanel.group`),//取消组合：组合
      subText: 'Ctrl + G',
      handler: props.elementInfo.groupId ? uncombineElements : combineElements,
      hide: !props.isMultiSelect,
    },
    {
      text: t(`editor.positionPanel.selectAll`),//全选
      subText: 'Ctrl + A',
      handler: selectAllElement,
    },
    {
      text: t(`editor.positionPanel.lock`),//锁定
      subText: 'Ctrl + L',
      handler: lockElement,
    },
    {
      text: t(`editor.positionPanel.delete`),//删除
      subText: 'Delete',
      handler: deleteElement,
    },
  ]
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "editable-element",
    ref: "elementRef",
    id: `editable-element-${__props.elementInfo.id}`,
    style: _normalizeStyle({
      zIndex: __props.elementIndex,
    })
  }, [
    (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(currentElementComponent)), {
      elementInfo: __props.elementInfo,
      selectElement: __props.selectElement,
      contextmenus: contextmenus
    }, null, 8, ["elementInfo", "selectElement"]))
  ], 12, _hoisted_1))
}
}

})